import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Agent } from "model/agent";
import { updateAgent } from "redux/slice/agents";
import { getUser } from "redux/selector";
import { AgentStaffType } from "type/agent-staff";
import { BioTab } from "component/shared/profile/tab-bio";
import { Switch } from "component/shared/switch";
import { LabeledField } from "./labeled-field";
import { styles } from "./styles";
import { TabHandle } from "./tab-handle";

interface Props extends WithStyles<typeof styles> {
	agent: Agent;
	isEnhancedBioEnabled?: boolean;
	onDirtyChange: () => void;
	/** The logged-in member viewing their own Profile page — switches copy to second person. */
	selfView?: boolean;
}

const useLocalStyles = makeStyles()((theme) => ({
	// Spacing between successive toggle rows (the shared Switch owns the switch/label/description layout).
	toggleRow: { marginBottom: theme.spacing(2) },
	// Subsection header: 16px bold (MAX-3867).
	subTitle: { fontSize: 16, fontWeight: 700, marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
	// Divider between the Staff Type and Agent Code subsections (MAX-3926).
	subDivider: { marginTop: theme.spacing(3) },
	// Link destination controls, indented under the toggle they depend on (MAX-4320).
	// Indented to line up with the toggle's label and description above it, not with the switch.
	linkDestination: { paddingLeft: theme.spacing(7), maxWidth: 420 },
	// Match the Roles checkboxes and the toggles: brand orange, not MUI's teal primary.
	radio: {
		color: theme.palette.custom.primary,
		"&.Mui-checked": { color: theme.palette.custom.primary },
	},
}));

const VISIBILITY = [
	{ key: "enableAgentLink", title: "Allow website visitors to view agent's bio page or custom URL", desc: "When enabled, this agent will be clickable on the agent list page, directing visitors to their bio page or a custom URL." },
	{ key: "showOnSignupDropDown", title: "Show agent in agent selector on capture forms", desc: "When enabled, this agent will appear as an option in the agent selector dropdown on lead capture forms." },
	{ key: "showImageOnPage", title: "Show agent information on featured listings", desc: "When enabled, this agent's information will display on featured listings associated with their agent ID." },
] as const;

// Same three toggles addressed to the member reading their own profile (MAX-4139).
const VISIBILITY_SELF = [
	{ key: "enableAgentLink", title: "Allow website visitors to view your bio page or custom URL", desc: "When enabled, you will be clickable on the agent list page, directing visitors to your bio page or a custom URL." },
	{ key: "showOnSignupDropDown", title: "Show you in the agent selector on capture forms", desc: "When enabled, you will appear as an option in the agent selector dropdown on lead capture forms." },
	{ key: "showImageOnPage", title: "Show your information on featured listings", desc: "When enabled, your information will display on featured listings associated with your agent ID." },
] as const;

/**
 * Website tab: new Display + Visibility sections (saved here via updateAgent), then the Agent Bio
 * Page which reuses the existing BioTab component — it already implements the Enhanced/legacy
 * branch, header/logo image upload (existing Upload tool), social, about, markets, testimonials
 * (+ modal) and its own save. We do NOT reimplement those.
 */
export const WebsiteTab = forwardRef<TabHandle, Props>(({ agent, selfView = false, classes = {}, isEnhancedBioEnabled, onDirtyChange }, ref) => {
	const { classes: local } = useLocalStyles();
	const dispatch = useAppThunkDispatch();
	const user = useSelector(getUser);
	// Self-view only, for the same reason as the other tier gates: member-detail is already live.
	const showAgentCode = !selfView
		|| !!(user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures);
	// A non-billable member has no bio page or featured listings of their own, so self-view offers
	// them only the capture-form toggle. Hidden toggles keep their stored values on save.
	const visibilityOptions = selfView
		? VISIBILITY_SELF.filter((v) => agent.ownsLeads || v.key === "showOnSignupDropDown")
		: VISIBILITY;

	const [staffTypeId, setStaffTypeId] = useState(agent.staffType?.id || AgentStaffType.default.id);
	const [agentCode, setAgentCode] = useState(agent.code || "");
	const [visibility, setVisibility] = useState({
		enableAgentLink: agent.enableAgentLink || false,
		showOnSignupDropDown: agent.showOnSignupDropDown || false,
		showImageOnPage: agent.showImageOnPage || false,
	});
	// MAX-4320: the link destination controls the MAX-3707 refresh dropped. Matches the legacy
	// AgentSettingsTab, including seeding the URL field from agentPageUrl so it shows the default
	// bio URL rather than an empty box.
	const deriveRedirectUrl = (a: Agent) =>
		(a.hasOwnSite ? a.redirectUrl || a.agentPageUrl : a.agentPageUrl) || "";
	const [hasOwnSite, setHasOwnSite] = useState(agent.hasOwnSite || false);
	const [redirectUrl, setRedirectUrl] = useState(() => deriveRedirectUrl(agent));
	// Held in a ref so the dirty check has a fixed baseline: saving dispatches agentLoaded, which
	// replaces the agent prop, and a baseline recomputed from that prop would shift mid-save.
	const initialRedirectUrlRef = useRef(deriveRedirectUrl(agent));
	const bioSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);
	const bioBeginSaveRef = useRef<(() => void) | undefined>(undefined);
	const bioAbortSaveRef = useRef<(() => void) | undefined>(undefined);
	const bioDirtyRef = useRef<(() => boolean) | undefined>(undefined);
	const bioCancelRef = useRef<(() => void) | undefined>(undefined);

	// Dirty when the Display/Visibility fields this tab owns OR the reused BioTab's fields change.
	const isDirty = () =>
		staffTypeId !== (agent.staffType?.id || AgentStaffType.default.id) ||
		agentCode !== (agent.code || "") ||
		visibility.enableAgentLink !== (agent.enableAgentLink || false) ||
		visibility.showOnSignupDropDown !== (agent.showOnSignupDropDown || false) ||
		visibility.showImageOnPage !== (agent.showImageOnPage || false) ||
		hasOwnSite !== (agent.hasOwnSite || false) ||
		redirectUrl !== initialRedirectUrlRef.current ||
		(bioDirtyRef.current ? bioDirtyRef.current() : false);

	useEffect(() => { onDirtyChange(); }, [onDirtyChange, staffTypeId, agentCode, visibility, hasOwnSite, redirectUrl]);

	useImperativeHandle(ref, () => ({
		isDirty,
		cancel: () => {
			setStaffTypeId(agent.staffType?.id || AgentStaffType.default.id);
			setAgentCode(agent.code || "");
			setVisibility({
				enableAgentLink: agent.enableAgentLink || false,
				showOnSignupDropDown: agent.showOnSignupDropDown || false,
				showImageOnPage: agent.showImageOnPage || false,
			});
			setHasOwnSite(agent.hasOwnSite || false);
			setRedirectUrl(initialRedirectUrlRef.current);
			// The bio fields live in BioTab's own state, so resetting this tab's is not enough.
			bioCancelRef.current?.();
		},
		save: async () => {
			// Save the Display/Visibility fields this tab owns. The bio's markets/social/etc. live in
			// BioTab's own state (not on this agent prop), so we must NOT send publicBio here — doing so
			// would write the stale prop value back and wipe the user's just-picked markets. BioTab saves
			// its own publicBio (including markets) below, and it saves last so it wins.
			// Freeze BioTab's testimonial state first. This PUT's response echoes the stored
			// testimonials, and the echo lands before BioTab's own save begins — without the
			// freeze it overwrites the user's pending testimonial edits (MAX-4319).
			bioBeginSaveRef.current?.();
			try {
				// .unwrap() so an API failure rejects here instead of resolving into a false success.
				await dispatch(updateAgent({
					agent: {
						...agent,
						publicBio: undefined,
						staffType: AgentStaffType.getById(staffTypeId),
						code: agentCode,
						...visibility,
						hasOwnSite,
						redirectUrl,
					},
				})).unwrap();
				// Re-baseline so the tab stops reporting dirty once these values are persisted.
				initialRedirectUrlRef.current = redirectUrl;
			} catch(error) {
				// Release the freeze armed above; BioTab's own save never ran to release it.
				bioAbortSaveRef.current?.();
				throw error;
			}
			if (bioSaveRef.current) {
				await bioSaveRef.current();
			}
		},
	}));

	return (
		<div>
			{/* Display */}
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Display</Mui.Typography>
				<Mui.Typography className={local.subTitle}>Staff Type</Mui.Typography>
				<Mui.Typography className={classes.sectionDescription}>{selfView ? "Specifies your role as it appears on your website's agent list." : "Specifies the agent's role as it appears on your website's agent list."}</Mui.Typography>
				<Mui.Grid container spacing={2} className={classes.formColumn}>
					<Mui.Grid item xs={12}>
						<LabeledField select value={String(staffTypeId)} onChange={(v) => setStaffTypeId(Number(v))}>
							{AgentStaffType.values().map((t) => <Mui.MenuItem key={t.id} value={t.id}>{t.label}</Mui.MenuItem>)}
						</LabeledField>
					</Mui.Grid>
				</Mui.Grid>
				{showAgentCode && <>
					<Mui.Divider className={local.subDivider} />
					<Mui.Typography className={local.subTitle}>Agent Code</Mui.Typography>
					<Mui.Typography className={classes.sectionDescription}>{selfView ? "Display your agent identification code (e.g. license number, DRE number) on your bio page and featured listings." : "Display the agent's identification code (e.g. license number, DRE number) on their bio page and featured listings."}</Mui.Typography>
					<Mui.Grid container spacing={2} className={classes.formColumn}>
						<Mui.Grid item xs={12}>
							<LabeledField label="Agent Code" value={agentCode} onChange={setAgentCode} />
						</Mui.Grid>
					</Mui.Grid>
				</>}
			</div>

			{/* Visibility */}
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Visibility</Mui.Typography>
				{visibilityOptions.map((v) => (
					<div key={v.key} className={local.toggleRow}>
						<Switch
							checked={visibility[v.key]}
							label={v.title}
							description={v.desc}
							onChange={(checked) => setVisibility({ ...visibility, [v.key]: checked })}
						/>
						{/* MAX-4320: link destination, shown under the toggle it belongs to. Admin-only,
						    as it was on the legacy form. */}
						{v.key === "enableAgentLink" && user.admin && visibility.enableAgentLink && (
							<Mui.Box className={local.linkDestination}>
								<Mui.RadioGroup
									value={hasOwnSite ? "custom" : "bio"}
									onChange={(event) => setHasOwnSite(event.target.value === "custom")}
								>
									<Mui.FormControlLabel value="bio" control={<Mui.Radio className={local.radio} />} label="Link to Bio" />
									<Mui.FormControlLabel value="custom" control={<Mui.Radio className={local.radio} />} label="Link to Custom URL" />
								</Mui.RadioGroup>
								<Mui.Collapse in={hasOwnSite}>
									<LabeledField
										label="Website URL"
										required
										value={redirectUrl}
										onChange={setRedirectUrl}
									/>
								</Mui.Collapse>
							</Mui.Box>
						)}
					</div>
				))}
			</div>

			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Agent Bio Page</Mui.Typography>
				<div className={classes.bioPage}>
					<BioTab agent={agent} isEnhancedBioEnabled={isEnhancedBioEnabled} hideSaveBar saveRef={bioSaveRef} beginSaveRef={bioBeginSaveRef} abortSaveRef={bioAbortSaveRef} dirtyRef={bioDirtyRef} cancelRef={bioCancelRef} onDirtyChange={onDirtyChange} />
				</div>
			</div>
		</div>
	);
});
